import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Backs a "Buy X Get Y Free" automatic discount by auto-adding/removing the
 * free gift line item, since Shopify discounts can only re-price items that
 * are already in the cart — they can never add a line item on their own.
 *
 * IMPORTANT: keep triggerQtyPerUnit / giftQtyPerUnit / maxMultiplier in sync
 * with the actual discount config in Shopify admin (Discounts > the Buy X
 * Get Y discount). If the discount has a "maximum number of times per
 * order" limit and maxMultiplier here doesn't match it, this script will
 * keep adding "free" units past what the discount actually covers, and the
 * customer will be charged full price for the extra units. Confirm that
 * setting before relying on this in production.
 *
 * @typedef {Object} GiftBundle
 * @property {string} triggerVariantId - Variant that unlocks the gift.
 * @property {number} triggerQtyPerUnit - Quantity of the trigger needed per gift unit.
 * @property {string} giftVariantId - Variant given away free.
 * @property {number} giftQtyPerUnit - Free quantity granted per trigger unit.
 * @property {number | null} maxMultiplier - Cap on how many times the bundle can apply, or null for no cap.
 * @property {string} giftPropertyValue - Unique tag identifying this bundle's auto-added line.
 */

/** @type {GiftBundle[]} */
const GIFT_BUNDLES = [
  {
    // U CHECK SUGAR STRIPS 50S — Pack Qty: 2
    triggerVariantId: '47019468292379',
    triggerQtyPerUnit: 2,
    // U CHECK BGM — Pack Qty: 1
    giftVariantId: '49828177019163',
    giftQtyPerUnit: 1,
    maxMultiplier: null,
    giftPropertyValue: 'sugar-strips-bgm-bundle',
  },
];

// Underscore-prefixed properties are hidden from the cart UI by this theme (see cart-products.liquid).
const GIFT_PROPERTY_KEY = '_auto_free_gift';

class FreeGiftAutomator {
  #syncing = false;
  #pendingResync = false;

  constructor() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#sync();
  }

  /** @param {CartUpdateEvent} event */
  #onCartUpdate = (event) => {
    if (event.detail?.data?.source === 'free-gift-automator') return;
    this.#sync();
  };

  async #sync() {
    if (this.#syncing) {
      this.#pendingResync = true;
      return;
    }
    this.#syncing = true;

    try {
      let cart = await this.#fetchCart();
      let mutated = false;

      for (const bundle of GIFT_BUNDLES) {
        const didMutate = await this.#reconcileBundle(cart, bundle);
        if (didMutate) mutated = true;
      }

      if (mutated) {
        cart = await this.#fetchCart();
        this.#broadcastUpdate(cart);
      }
    } catch (error) {
      console.error('[free-gift-automator]', error);
    } finally {
      this.#syncing = false;

      if (this.#pendingResync) {
        this.#pendingResync = false;
        this.#sync();
      }
    }
  }

  async #fetchCart() {
    const response = await fetch('/cart.js');
    return response.json();
  }

  /**
   * @param {any} cart
   * @param {GiftBundle} bundle
   * @returns {Promise<boolean>} Whether the cart was mutated.
   */
  async #reconcileBundle(cart, bundle) {
    const { triggerVariantId, triggerQtyPerUnit, giftVariantId, giftQtyPerUnit, maxMultiplier, giftPropertyValue } =
      bundle;

    const triggerQty = cart.items
      .filter((/** @type {any} */ item) => item.variant_id.toString() === triggerVariantId)
      .reduce((/** @type {number} */ sum, /** @type {any} */ item) => sum + item.quantity, 0);

    let multiplier = Math.floor(triggerQty / triggerQtyPerUnit);
    if (typeof maxMultiplier === 'number') {
      multiplier = Math.min(multiplier, maxMultiplier);
    }
    const expectedGiftQty = multiplier * giftQtyPerUnit;

    const giftLine = cart.items.find(
      (/** @type {any} */ item) =>
        item.variant_id.toString() === giftVariantId && item.properties?.[GIFT_PROPERTY_KEY] === giftPropertyValue
    );
    const currentGiftQty = giftLine ? giftLine.quantity : 0;

    if (expectedGiftQty === currentGiftQty) return false;

    if (expectedGiftQty > currentGiftQty) {
      await this.#addGift(giftVariantId, expectedGiftQty - currentGiftQty, giftPropertyValue);
    } else if (giftLine) {
      await this.#setLineQuantity(giftLine.key, expectedGiftQty);
    }

    return true;
  }

  /**
   * @param {string} variantId
   * @param {number} quantity
   * @param {string} giftPropertyValue
   */
  async #addGift(variantId, quantity, giftPropertyValue) {
    const body = JSON.stringify({
      id: variantId,
      quantity,
      properties: {
        [GIFT_PROPERTY_KEY]: giftPropertyValue,
      },
    });

    await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
  }

  /**
   * @param {string} lineKey
   * @param {number} quantity
   */
  async #setLineQuantity(lineKey, quantity) {
    const body = JSON.stringify({ id: lineKey, quantity });

    await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
  }

  /** @param {any} cart */
  #broadcastUpdate(cart) {
    document.dispatchEvent(
      new CartUpdateEvent(cart, 'free-gift-automator', {
        source: 'free-gift-automator',
        itemCount: cart.item_count,
      })
    );
  }
}

if (!window.freeGiftAutomator) {
  window.freeGiftAutomator = new FreeGiftAutomator();
}
